/**
 * Product Form (Full File Replacement)
 * Includes HARD FIX: resolve correct variant id from selected options before submit
 *
 * SELVANE CUSTOM FIX v2.0:
 * - Supports both options[Color] format AND direct Color/Size format
 * - Fetches variants from product JSON endpoint if not found in page
 * - Works with Agile theme's radio button structure
 *
 * Notes:
 * - This file assumes Shopify theme globals exist:
 *   - fetchConfig
 *   - routes.cart_add_url / window.routes.cart_url
 *   - publish / PUB_SUB_EVENTS
 *   - closePopup (if cart page)
 */

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form) return;

        const idInput = this.form.querySelector("[name=id]");
        if (idInput) idInput.disabled = false;

        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");

        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector("cart-drawer") && this.submitButton) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";

        // internal state
        this.error = false;
        
        // Cache variants data
        this.variantsCache = null;
      }

      /**
       * Get variants data from various sources
       */
      async getVariantsData() {
        // Return cached data if available
        if (this.variantsCache) return this.variantsCache;
        
        // Try to find variants JSON in the page
        let jsonScript =
          this.querySelector('script[type="application/json"]') ||
          this.closest("section")?.querySelector('script[type="application/json"]') ||
          document.querySelector('script[type="application/json"][data-product-variants]');

        if (jsonScript) {
          try {
            const data = JSON.parse(jsonScript.textContent || "[]");
            if (Array.isArray(data) && data.length > 0 && data[0].id) {
              this.variantsCache = data;
              return data;
            }
          } catch (e) {
            // Not valid variants JSON, continue
          }
        }

        // Try to fetch from product JSON endpoint
        const productHandle = this.getProductHandle();
        if (productHandle) {
          try {
            const response = await fetch(`/products/${productHandle}.js`);
            const product = await response.json();
            if (product && product.variants) {
              this.variantsCache = product.variants;
              return product.variants;
            }
          } catch (e) {
            console.warn("SELVANE: Failed to fetch product variants", e);
          }
        }

        return [];
      }

      /**
       * Get product handle from URL or page
       */
      getProductHandle() {
        // Try to get from URL
        const match = window.location.pathname.match(/\/products\/([^\/\?]+)/);
        if (match) return match[1];
        
        // Try to get from form action
        const formAction = this.form?.action || "";
        const actionMatch = formAction.match(/\/products\/([^\/\?]+)/);
        if (actionMatch) return actionMatch[1];
        
        return null;
      }

      /**
       * HARD FIX v2.0:
       * Before submit, compute correct variant id from selected options + variants JSON,
       * then write it into <input name="id"> in THIS form.
       *
       * Supports both:
       * - options[Color] format (standard Shopify)
       * - Color/Size format (Agile theme)
       */
      async resolveVariantIdBeforeSubmit() {
        try {
          if (!this.form) return;

          const idInput = this.form.querySelector('[name="id"]');
          if (!idInput) return;

          // Get the form ID to find associated radio buttons
          const formId = this.form.id;
          
          // Collect selected options - try multiple formats
          const selected = {};
          
          // Method 1: Look for options[Name] format inputs within the form
          const optionInputs = Array.from(
            this.form.querySelectorAll('select[name^="options["], input[name^="options["]:checked')
          );
          
          optionInputs.forEach((el) => {
            const match = el.name.match(/options\[([^\]]+)\]/);
            if (match) {
              selected[match[1]] = el.value;
            }
          });

          // Method 2: Look for direct name format (Color, Size) - radio buttons associated via form attribute
          if (Object.keys(selected).length === 0 && formId) {
            // Find radio buttons that reference this form
            const associatedRadios = document.querySelectorAll(`input[type="radio"][form="${formId}"]:checked`);
            associatedRadios.forEach((radio) => {
              if (radio.name && !radio.name.startsWith("options[")) {
                selected[radio.name] = radio.value;
              }
            });
          }

          // Method 3: Look for radio buttons within the closest product-info or section
          if (Object.keys(selected).length === 0) {
            const container = this.closest('product-info') || this.closest('section') || this.closest('.product');
            if (container) {
              const radios = container.querySelectorAll('input[type="radio"]:checked');
              radios.forEach((radio) => {
                if (radio.name && !radio.name.startsWith("options[")) {
                  selected[radio.name] = radio.value;
                }
              });
            }
          }

          // Method 4: Global fallback - find all checked radios with Color/Size names
          if (Object.keys(selected).length === 0) {
            ['Color', 'Size', 'color', 'size', 'Style', 'style', 'Material', 'material'].forEach(name => {
              const radio = document.querySelector(`input[type="radio"][name="${name}"]:checked`);
              if (radio) {
                selected[name] = radio.value;
              }
            });
          }

          if (Object.keys(selected).length === 0) {
            console.log("SELVANE: No selected options found");
            return;
          }

          console.log("SELVANE: Selected options:", selected);

          // Get variants data
          const variants = await this.getVariantsData();
          
          if (!variants || variants.length === 0) {
            console.warn("SELVANE: No variants data found");
            return;
          }

          console.log("SELVANE: Found", variants.length, "variants");

          // Build ordered values array based on option names
          // Shopify variants have option1, option2, option3 which map to options[0], options[1], options[2]
          const orderedValues = [];
          
          // Try to determine option order from the first variant
          const firstVariant = variants[0];
          if (firstVariant) {
            // Check if variant has option1, option2, option3 properties
            for (let i = 1; i <= 3; i++) {
              const optionKey = `option${i}`;
              if (firstVariant[optionKey] !== null && firstVariant[optionKey] !== undefined) {
                // Find which selected option matches this position
                // We need to match by value since we don't know the option name order
                let foundValue = null;
                for (const [name, value] of Object.entries(selected)) {
                  // Check if this value exists in any variant at this position
                  const hasValueAtPosition = variants.some(v => v[optionKey] === value);
                  if (hasValueAtPosition && !orderedValues.includes(value)) {
                    foundValue = value;
                    break;
                  }
                }
                if (foundValue) {
                  orderedValues.push(foundValue);
                }
              }
            }
          }

          // Alternative: Match using variant.options array
          if (orderedValues.length === 0) {
            const selectedValues = Object.values(selected);
            orderedValues.push(...selectedValues);
          }

          console.log("SELVANE: Ordered values:", orderedValues);

          // Find matching variant
          const match = variants.find((v) => {
            if (!v) return false;
            
            // Try matching with options array
            if (Array.isArray(v.options)) {
              if (v.options.length < orderedValues.length) return false;
              for (let i = 0; i < orderedValues.length; i++) {
                if ((v.options[i] || "") !== (orderedValues[i] || "")) return false;
              }
              return true;
            }
            
            // Try matching with option1, option2, option3
            for (let i = 0; i < orderedValues.length; i++) {
              const optionKey = `option${i + 1}`;
              if ((v[optionKey] || "") !== (orderedValues[i] || "")) return false;
            }
            return true;
          });

          if (match && match.id) {
            console.log("SELVANE: Found matching variant:", match.id, match.title || match.options?.join(" / "));
            idInput.value = String(match.id);
          } else {
            console.warn("SELVANE: No matching variant found for:", orderedValues);
          }
        } catch (e) {
          // Do not block submit
          console.warn("SELVANE: resolveVariantIdBeforeSubmit failed", e);
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // ✅ HARD FIX: ensure id matches current options before building FormData
        await this.resolveVariantIdBeforeSubmit();

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading-overlay__spinner")?.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        if (this.cart) {
          try {
            formData.append(
              "sections",
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append("sections_url", window.location.pathname);

            if (this.submitButton.id != "ProductSubmitButton-product-quickview") {
              this.cart.setActiveElement(document.activeElement);
            }
          } catch (e) {
            // non-fatal; proceed without sections if theme structure differs
          }
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              if (!soldOutMessage) return;

              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span")?.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes?.cart_url || window.routes?.cart || window.location.origin + "/cart";
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            }

            this.error = false;

            const is_cart_page = document.body.classList.contains("template-cart");

            if (!is_cart_page) {
              this.cart.renderContents(response);
            } else {
              // some themes use closePopup
              if (typeof closePopup !== "undefined" && closePopup?.hide) closePopup.hide();
            }

            const shopableModal = document.querySelector("shopable-modal");
            if (shopableModal) {
              shopableModal.classList.remove("show");
              setTimeout(() => {
                shopableModal.remove();
                document.body.classList.remove("modal-open");
              }, 300);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");

            if (this.cart && this.cart.classList.contains("is-empty")) {
              this.cart.classList.remove("is-empty");
            }

            if (!this.error) {
              this.submitButton.removeAttribute("aria-disabled");
            }

            this.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}